// A dumb donut with one drill step: slices under 5 % of the total collapse into a
// clickable "Others" slice whose children are shown on click. No emits — a slice click
// calls the `onSelect` function prop (`onSelect(id)`, or `onSelect(null)` when the
// already-selected slice is clicked again).
//
//   props:  title          (String)   card title; rendered as-is, already translated
//           seriesName     (String)   ECharts series name; falls back to `title`
//           dataProvider   (Function) () => Promise<Array<{ teamId, name, value }>>
//           onSelect       (Function) (teamId | null) => Promise<void>
//           chartFontColor (String)   slice-label colour; a change reloads
//           filterContext  (Object)   the filter-context duck type
//           dataLoaded     (Boolean)  gates the initial load
//           bare           (Boolean)  opt-in: no card/title of its own (the page owns the card)
//           drill          (Object)   opt-in: a second level under every slice —
//                                     { dataProvider(parentId), onSelect(id, parentId), seriesName? }
//           exportFilename (String)   opt-in: PNG export under this name — with the card, a download
//                                     button in the title row; '' = none
//           exportTitle    (String)   the PNG's title; '' = `title`, else `seriesName`
//   setup:  downloadPng()  saves the PNG (one legend entry per slice) — the button's action, and
//                          callable through a template ref (a `bare` donut's page owns the button)
//
// Full contract: ../../../README.md → BaseDonutChart. The logic lives in ./setup.js.
//
// The echarts module registration is wiring for the `VChart` registration below — both
// are what the template's `<v-chart>` needs — so it lives here and ./setup.js stays
// pure logic.
import { use } from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { PieChart } from "echarts/charts";
import { TooltipComponent, TitleComponent, LegendComponent } from "echarts/components";
import VChart from "vue-echarts";
import setup from './setup.js';

use([CanvasRenderer, PieChart, TooltipComponent, TitleComponent, LegendComponent]);

export default {
  name: 'base-donut-chart',
  props: {
    // Rendered as-is — already translated by the page
    title: {
      type: String,
      default: ''
    },
    // ECharts series name (tooltips/legend); rendered as-is — already translated by
    // the page. Falls back to `title` so a caller may pass just one.
    seriesName: {
      type: String,
      default: ''
    },
    // () => Promise<Array<{ teamId, name, value }>>  — called with no arguments; the
    // provider decides whether to read the filtered view (see the README)
    dataProvider: {
      type: Function,
      required: true
    },
    // (teamId | null) => Promise<void>; null clears this chart's filter
    onSelect: {
      type: Function,
      required: true
    },
    chartFontColor: {
      type: String,
      default: '#000000'
    },
    filterContext: {
      type: Object,
      required: true
    },
    dataLoaded: {
      type: Boolean,
      default: false
    },
    // Opt-in (issue-analysis, IA_ABSORPTION_PLAN Phase 4 step 12b): render the bare chart in
    // the root <div> — no card, no title — for a page that owns the card (issue-analysis's
    // tabbed donut cards). Off = the card with `title`, exactly as before.
    bare: {
      type: Boolean,
      default: false
    },
    // Opt-in (issue-analysis's Created By donut, step 12b): a second level under every slice.
    //   { dataProvider: (parentId) => Promise<rows>  — the same row shape as `dataProvider`,
    //     onSelect: (id, parentId) => Promise<void>   — filter by a second-level slice,
    //     seriesName?: String                          — already translated }
    // A top-level slice click shows its children, then calls onSelect(parentId). A child click
    // calls drill.onSelect(id, parentId); a click on the selected child (or on a slice without a
    // key) returns to the top level and calls onSelect(null). Null = one level, as before.
    drill: {
      type: Object,
      default: null
    },
    // Opt-in (2026-10-16, PNG export on every chart): the PNG's file name. With the card (not
    // `bare`) the title row becomes title + a download button; a `bare` donut renders no button —
    // its page calls `downloadPng()` through a template ref. '' = no export, the card as before.
    exportFilename: {
      type: String,
      default: ''
    },
    // The PNG's title; '' = `title`, else `seriesName`
    exportTitle: {
      type: String,
      default: ''
    }
  },
  // `<v-chart>` was auto-registered from the import by `<script setup>`; a classic
  // component has to register it explicitly or Vue logs "Failed to resolve component:
  // v-chart" and renders nothing. Vuetify tags need no entry — vite-plugin-vuetify
  // rewrites the compiled render code, not the script block.
  components: { VChart },
  setup,
};
